import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

/** Loads a base64 data-URI PDF via pdf.js. Returns { pdf, numPages }. */
export function usePdfDocument(pdfDataUri) {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPdf(null);
    setNumPages(0);
    if (!pdfDataUri) return undefined;
    let bytes;
    try {
      const base64 = pdfDataUri.split(',')[1];
      bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    } catch {
      return undefined;
    }
    const task = pdfjsLib.getDocument({ data: bytes });
    task.promise
      .then((doc) => {
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      task.destroy?.();
    };
  }, [pdfDataUri]);

  return { pdf, numPages };
}

/**
 * Renders one PDF page to a canvas sized to the container width, keeping the
 * page's real aspect ratio. `children` render as an absolute overlay on top
 * (used for field placement / signer inputs).
 */
export function PdfPageCanvas({ pdf, pageNumber, className = '', children }) {
  const canvasRef = useRef(null);
  const [ratio, setRatio] = useState('612 / 792');

  useEffect(() => {
    if (!pdf || !pageNumber) return undefined;
    let cancelled = false;
    let renderTask = null;
    pdf
      .getPage(pageNumber)
      .then((page) => {
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.5 });
        setRatio(`${viewport.width} / ${viewport.height}`);
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
        renderTask.promise.catch(() => {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch { /* noop */ }
    };
  }, [pdf, pageNumber]);

  return (
    <div
      className={`relative w-full bg-white border rounded overflow-hidden ${className}`}
      style={{ aspectRatio: ratio }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      {children}
    </div>
  );
}

export default PdfPageCanvas;
